import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Component,
  type ReactNode,
} from "react";
import { Link } from "../../lib/router";
import { ConvexError } from "convex/values";
import * as Sentry from "@sentry/react";
import { useHumanTaskQueue, type HumanTaskQueueState } from "../../api/hooks/useHumanTaskQueue";
import { useDecisionsMutations } from "../../api/hooks/useDecisionsMutations";
import { HUMAN_TASK_QUEUE_LIMIT } from "../../api/constants/humanTask";
import {
  Icon,
  Button,
  Badge,
  SkeletonRow,
  EmptyState,
  ErrorState,
  RowSlideIn,
} from "../primitives";
import { CCModal } from "../cross-cutting/CCModal";
import { useToast } from "../providers/ToastProvider";
import type { DecisionItem } from "../../api/transforms/humanTask";
import type { Id } from "../../../../../convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// Error boundary — catches ConvexErrors thrown by useQuery during render.
// Renders its own fallback UI: empty state for forbidden errors, error state
// (with working retry) for all other errors.
// The outer component resets this boundary by changing the `key` prop.
// componentDidCatch forwards non-ConvexError exceptions to Sentry.
// ---------------------------------------------------------------------------

interface ErrorBoundaryState {
  errorKind: "forbidden" | "error" | null;
}

class DecisionsTrayErrorBoundary extends Component<
  { children: ReactNode; onRetry: () => void },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { errorKind: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    if (error instanceof ConvexError) {
      const code = (error.data as { code?: string })?.code;
      if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
        return { errorKind: "forbidden" };
      }
      return { errorKind: "error" };
    }
    return { errorKind: "error" };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // ConvexErrors are expected (auth failures, transient network issues,
    // documented row-not-found). Their visual is handled by the boundary's
    // own fallback UI; logging to Sentry would create noise.
    // Non-ConvexErrors are application bugs (TypeError, undefined access,
    // bad render) — these belong in Sentry as actionable incidents.
    if (!(error instanceof ConvexError)) {
      try {
        Sentry.captureException(error, {
          extra: {
            componentStack: info.componentStack,
            errorKind: this.state.errorKind,
            // Do not include userId or humanTaskId here — componentDidCatch runs
            // in the React commit phase, where those values are not in scope.
          },
          tags: {
            component: "DecisionsTrayErrorBoundary",
            errorType: "ReactRenderError",
          },
        });
      } catch {
        // Sentry capture itself must never crash the error boundary.
      }
    }
  }

  render(): ReactNode {
    if (this.state.errorKind === "forbidden") {
      // Same visual as the empty state. Forbidden is not retriable without a
      // page reload or re-authentication, so no retry button is shown.
      return (
        <EmptyState>
          <p>No decisions waiting — head to your Command Center</p>
          <Link href="#/today">Go to Today</Link>
        </EmptyState>
      );
    }
    if (this.state.errorKind === "error") {
      return (
        <ErrorState
          message="Couldn't load your decisions. Try again."
          onRetry={this.props.onRetry}
        />
      );
    }
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Top-level component props
// ---------------------------------------------------------------------------

export interface DecisionsTrayProps {
  /**
   * Called whenever the queue count changes. Used by <TopNav> to render the
   * bell badge count without mounting a second subscription.
   * Receives 0 on empty, error, and forbidden states.
   * Not called during loading (preserves previous count to avoid badge flicker).
   */
  onCountChange: (count: number) => void;
}

// ---------------------------------------------------------------------------
// Inner tray body — rendered inside the error boundary
// ---------------------------------------------------------------------------

function DecisionsTrayBody({
  onCountChange,
  onRetry,
}: {
  onCountChange: (count: number) => void;
  // TEST-ONLY: this prop is passed to <ErrorState> in the error case below.
  // At runtime the error case is unreachable because <DecisionsTrayErrorBoundary>
  // catches errors during render. This prop wires up retry only when the unit
  // test injects { status: "error" } via a mock hook.
  onRetry: () => void;
}) {
  const queueState = useHumanTaskQueue();
  const { resolve, snooze, respond } = useDecisionsMutations();
  const { showToast } = useToast();

  const [respondingTo, setRespondingTo] = useState<Id<"humanTasks"> | null>(null);
  const [respondText, setRespondText] = useState("");
  const [snoozeOpenFor, setSnoozeOpenFor] = useState<Id<"humanTasks"> | null>(null);

  // Derive a primitive scalar from the queue state for the badge effect.
  // React compares effect dependencies by Object.is — primitive values are
  // compared by value. This prevents the effect from firing on every render
  // simply because useHumanTaskQueue returned a new object reference.
  // undefined means "loading" — preserve the previous badge count.
  const countForBadge: number | undefined = useMemo(() => {
    switch (queueState.status) {
      case "ready":     return queueState.count;
      case "empty":
      case "error":
      case "forbidden": return 0;
      case "loading":   return undefined;  // preserve previous count
      default: {
        // Forward-compatibility guard for any future variant added to the union.
        return 0;
      }
    }
  }, [queueState]);

  useEffect(() => {
    if (countForBadge !== undefined) {
      onCountChange(countForBadge);
    }
  }, [countForBadge, onCountChange]);

  // ---------------------------------------------------------------------------
  // Mutation handlers with optimistic feedback
  // ---------------------------------------------------------------------------

  const handleResolve = useCallback(
    async (item: DecisionItem) => {
      try {
        // item.id is typed Id<"humanTasks"> — no cast needed.
        await resolve({ humanTaskId: item.id });
        showToast({ message: "Decision resolved", kind: "success" });
      } catch (err) {
        const code = (err instanceof ConvexError)
          ? (err.data as { code?: string })?.code
          : "UNKNOWN";
        if (code !== "NOT_FOUND") {
          // NOT_FOUND means the row is already gone (race between the
          // subscription tick and the click). Resolve was already effectively
          // achieved — silently suppress the error toast.
          showToast({ message: "Couldn't resolve this decision. Try again.", kind: "error" });
        }
      }
    },
    [resolve, showToast],
  );

  const handleSnooze = useCallback(
    async (humanTaskId: Id<"humanTasks">, durationMs: number) => {
      // Optimistic close: dismiss the picker before the mutation resolves.
      // This is the standard UX trade-off for low-stakes actions: keeping the
      // picker open while waiting for the network would create a "stuck open
      // on slow network" problem. On failure, the picker is already closed and
      // the user must click Snooze again to retry — acceptable UX.
      setSnoozeOpenFor(null);
      try {
        await snooze({ humanTaskId, until: Date.now() + durationMs });
      } catch (err) {
        showToast({ message: "Couldn't snooze this decision. Try again.", kind: "error" });
      }
    },
    [snooze, showToast],
  );

  const handleRespond = useCallback(
    async () => {
      if (!respondingTo || respondText.trim().length === 0) return;
      try {
        await respond({ humanTaskId: respondingTo, payload: respondText });
        showToast({ message: "Reply sent — clarification still open", kind: "success" });
        setRespondingTo(null);
        setRespondText("");
      } catch (err) {
        // Modal stays open on failure with the user's text intact, ready for retry.
        showToast({ message: "Couldn't send your reply. Try again.", kind: "error" });
      }
    },
    [respond, respondingTo, respondText, showToast],
  );

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  if (queueState.status === "loading") {
    return (
      <div aria-busy="true">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (queueState.status === "empty" || queueState.status === "forbidden") {
    // TEST-ONLY: "forbidden" as a hook return value is only possible via unit
    // test mock injection. At runtime, FORBIDDEN ConvexErrors are caught by
    // <DecisionsTrayErrorBoundary> above this component which renders the same
    // <EmptyState> visual. Both paths produce the same UX.
    return (
      <EmptyState>
        <p>No decisions waiting — head to your Command Center</p>
        <Link href="#/today">Go to Today</Link>
      </EmptyState>
    );
  }

  if (queueState.status === "error") {
    // TEST-ONLY: "error" as a hook return value is only possible via unit
    // test mock injection. At runtime, ConvexErrors are caught by the
    // <DecisionsTrayErrorBoundary> above this component. The onRetry prop is
    // wired so the test path can verify the retry callback fires.
    return (
      <ErrorState
        message="Couldn't load your decisions. Try again."
        onRetry={onRetry}
      />
    );
  }

  // status === "ready"
  const visibleRows = queueState.data.slice(0, 5);
  const overflowCount = queueState.count - 5;

  // Documented limitation (M5): queueState.count is bounded by
  // HUMAN_TASK_QUEUE_LIMIT (50). When count === HUMAN_TASK_QUEUE_LIMIT, the
  // actual database row count is unknown and may exceed 50. In that case the
  // component shows t("component.see-all") without a number. For the realistic use case
  // (1–15 pending tasks), the limit is never reached and the count is exact.
  const isAtLimit = queueState.count >= HUMAN_TASK_QUEUE_LIMIT;

  return (
    <>
      <ul role="list">
        {visibleRows.map((item) => (
          <RowSlideIn key={item.id}>
            <li>
              <Badge kind={item.kind}>{item.kind}</Badge>
              <p>{item.title}</p>
              <p>{item.subtitle}</p>
              <time>{item.relativeTime}</time>
              <div role="group">
                {item.canResolve && (
                  <Button onClick={() => handleResolve(item)}>Resolve</Button>
                )}
                {item.canSnooze && (
                  <Button onClick={() => setSnoozeOpenFor(item.id)}>
                    Snooze
                  </Button>
                )}
                {item.canRespond && (
                  <Button onClick={() => setRespondingTo(item.id)}>
                    Respond
                  </Button>
                )}
                {snoozeOpenFor === item.id && (
                  <SnoozePicker
                    onSelect={(ms) => handleSnooze(item.id, ms)}
                    onDismiss={() => setSnoozeOpenFor(null)}
                  />
                )}
              </div>
            </li>
          </RowSlideIn>
        ))}
      </ul>
      {overflowCount > 0 && (
        <Link href="#/today">
          {isAtLimit ? t("component.see-all") : `See all (${overflowCount} more)`}
        </Link>
      )}
      {respondingTo !== null && (
        <CCModal
          title="Send a reply"
          onClose={() => { setRespondingTo(null); setRespondText(""); }}
        >
          <textarea
            value={respondText}
            onChange={(e) => setRespondText(e.target.value)}
            maxLength={4096}
            placeholder="Type your reply…"
            aria-label="Your reply"
          />
          <p>{respondText.length}/4096</p>
          <Button onClick={() => { setRespondingTo(null); setRespondText(""); }}>
            Cancel
          </Button>
          <Button
            onClick={handleRespond}
            disabled={respondText.trim().length === 0}
          >
            Submit
          </Button>
        </CCModal>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Snooze preset picker
// ---------------------------------------------------------------------------

const SNOOZE_PRESETS: { label: string; ms: number }[] = [
  { label: "15 minutes", ms: 15 * 60 * 1000 },
  { label: "1 hour",     ms: 60 * 60 * 1000 },
  { label: "4 hours",    ms: 4 * 60 * 60 * 1000 },
  { label: "1 day",      ms: 24 * 60 * 60 * 1000 },
];

/**
 * Inline menu of snooze duration presets.
 *
 * Uses role="menu" / role="menuitem" — the W3C ARIA pattern for a set of
 * action commands. The native <Button> focusability and Tab navigation work
 * correctly with the menu role; arrow-key navigation is not required for a
 * menu (unlike role="listbox").
 */
function SnoozePicker({
  onSelect,
  onDismiss,
}: {
  onSelect: (ms: number) => void;
  onDismiss: () => void;
}) {
  return (
    <div role="menu" aria-label="Snooze duration">
      {SNOOZE_PRESETS.map((p) => (
        <Button key={p.label} role="menuitem" onClick={() => onSelect(p.ms)}>
          {p.label}
        </Button>
      ))}
      <Button role="menuitem" onClick={onDismiss}>Cancel</Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level exported component
// ---------------------------------------------------------------------------

/**
 * Decisions Tray overlay component.
 *
 * Mounted inside <TopNav> at the bell icon slot. Shows up to 5 pending
 * human-tasks for the signed-in user. Opens and closes on bell-icon click.
 * Calls onCountChange whenever the queue length changes so <TopNav> can
 * render the badge count without a second subscription.
 *
 * Error recovery: when the inner <DecisionsTrayErrorBoundary> enters an error
 * state, incrementing errorBoundaryKey via handleRetry causes React to unmount
 * and remount the boundary with a fresh state, clearing the error.
 */
export function DecisionsTray({ onCountChange }: DecisionsTrayProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  // Incrementing this key resets the error boundary via React's key-based
  // unmount/remount behavior — no external library needed.
  const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleRetry = useCallback(() => {
    setErrorBoundaryKey((k) => k + 1);
  }, []);

  // Close on Escape — registered only when the tray is open. This avoids an
  // always-active document keydown listener on a top-navigation component.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  // Close on outside click — registered only when the tray is open.
  // mousedown (not click) is used to fire before the browser processes the
  // click, preventing race conditions with focus events.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  return (
    <div ref={overlayRef}>
      <button
        aria-label="Open decisions tray"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((v) => !v)}
      >
        <Icon name="bell" size={20} />
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="Decisions for you"
          aria-modal="true"
        >
          <header>
            <h2>Decisions for you</h2>
            <Button onClick={() => setIsOpen(false)} aria-label="Close tray"><Icon name="x" size={16} /></Button>
          </header>

          {/*
            key={errorBoundaryKey} resets the boundary on retry.
            The boundary renders its own fallback UI; no callback to this outer
            component is needed for rendering. handleRetry is passed so the
            boundary can wire it to its <ErrorState onRetry> button, and is
            also passed to the body for the test-only mock-injection path.
          */}
          <DecisionsTrayErrorBoundary key={errorBoundaryKey} onRetry={handleRetry}>
            <DecisionsTrayBody
              onCountChange={onCountChange}
              onRetry={handleRetry}
            />
          </DecisionsTrayErrorBoundary>
        </div>
      )}
    </div>
  );
}
